import React, { useState, memo } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
  Divider
} from '@mui/material';
import {
  FaCrown,
  FaTrash,
  FaEllipsisV,
  FaUser,
  FaUserShield,
  FaClock,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';

const TeamMembersList = memo(({ 
  members, 
  currentUser, 
  isCompanyAdmin, 
  onRemoveMember, 
  onUpdateRole,
  loading = false 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  const handleMenuOpen = (event, member) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  const handleRoleChange = (newRole) => {
    if (selectedMember && onUpdateRole) {
      onUpdateRole(selectedMember.id, newRole);
    }
    handleMenuClose();
  };

  const handleRemoveMember = () => {
    if (selectedMember && onRemoveMember) {
      onRemoveMember(selectedMember.id);
    }
    handleMenuClose();
  };

  const getStatusColor = (member) => {
    if (member.invitationStatus === 'pending') return 'warning';
    if (member.invitationStatus === 'accepted') return 'success';
    return 'default';
  };

  const getStatusIcon = (member) => {
    if (member.invitationStatus === 'pending') return <FaClock />;
    if (member.invitationStatus === 'accepted') return <FaCheckCircle />;
    return <FaTimesCircle />;
  };

  const getRoleIcon = (member) => {
    if (member.isCompanyAdmin) return <FaCrown />;
    return member.userRole === 'admin' ? <FaUserShield /> : <FaUser />;
  };

  const getRoleColor = (member) => {
    if (member.isCompanyAdmin) return 'warning';
    return member.userRole === 'admin' ? 'primary' : 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <Typography color="text.secondary">Loading team members...</Typography>
      </Box>
    );
  }

  if (!members || members.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography color="text.secondary" variant="body2">
          No team members found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} color="text.primary" mb={2}>
        Team Members ({members.length})
      </Typography>
      
      {members.map((member, index) => (
        <Box
          key={member.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            mb: 1.5,
            background: member.id === currentUser?.id 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
              : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: 2,
            border: member.id === currentUser?.id 
              ? '2px solid #667eea' 
              : '1px solid #e2e8f0',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }
          }}
        >
          {/* Member Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Box sx={{ 
                  bgcolor: getStatusColor(member) === 'success' ? '#10b981' : 
                           getStatusColor(member) === 'warning' ? '#f59e0b' : '#ef4444',
                  borderRadius: '50%',
                  width: 12,
                  height: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {getStatusIcon(member)}
                </Box>
              }
            >
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: member.id === currentUser?.id ? '#667eea' : '#3b9efc',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                {member.firstName?.[0]}{member.lastName?.[0]}
              </Avatar>
            </Badge>
            
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography 
                  fontWeight={600} 
                  color={member.id === currentUser?.id ? 'white' : 'text.primary'} 
                  fontSize={14}
                >
                  {member.firstName} {member.lastName}
                  {member.id === currentUser?.id && (
                    <Chip 
                      label="You" 
                      size="small" 
                      sx={{ 
                        ml: 1, 
                        height: 18, 
                        fontSize: 10,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white'
                      }} 
                    />
                  )}
                </Typography>
                {getRoleIcon(member) && (
                  <Box sx={{ 
                    color: member.id === currentUser?.id ? 'white' : 
                           getRoleColor(member) === 'warning' ? '#f59e0b' : '#3b9efc'
                  }}>
                    {getRoleIcon(member)}
                  </Box>
                )}
              </Box>
              
              <Typography 
                color={member.id === currentUser?.id ? 'rgba(255,255,255,0.8)' : 'text.secondary'} 
                fontSize={12}
              >
                {member.email}
              </Typography>
              
              {member.invitationStatus === 'pending' && (
                <Typography 
                  color={member.id === currentUser?.id ? 'rgba(255,255,255,0.8)' : '#f59e0b'} 
                  fontSize={11}
                  sx={{ mt: 0.5 }}
                >
                  Invitation pending
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Role and Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={member.userRole === 'admin' ? 'Admin' : 'Member'}
              size="small"
              color={getRoleColor(member)}
              icon={getRoleIcon(member)}
              sx={{ 
                fontSize: 11, 
                height: 24,
                fontWeight: 600
              }}
            />
            
            {isCompanyAdmin && member.id !== currentUser?.id && (
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, member)}
                  sx={{ 
                    color: member.id === currentUser?.id ? 'white' : 'text.secondary',
                    '&:hover': {
                      bgcolor: member.id === currentUser?.id ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <FaEllipsisV size={12} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
      
      {/* Role Management Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2
          }
        }}
      >
        <MenuItem onClick={() => handleRoleChange('admin')}>
          <ListItemIcon>
            <FaUserShield size={14} />
          </ListItemIcon>
          <ListItemText 
            primary="Make Admin" 
            secondary="Full access to team management"
          />
        </MenuItem>
        
        <MenuItem onClick={() => handleRoleChange('member')}>
          <ListItemIcon>
            <FaUser size={14} />
          </ListItemIcon>
          <ListItemText 
            primary="Make Member" 
            secondary="Limited access to team features"
          />
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleRemoveMember} sx={{ color: '#ef4444' }}>
          <ListItemIcon>
            <FaTrash size={14} />
          </ListItemIcon>
          <ListItemText 
            primary="Remove Member" 
            secondary="Remove from team"
          />
        </MenuItem>
      </Menu>
    </Box>
  );
});

export default TeamMembersList; 